import {
  EQUIPMENT_INSUFFICIENT_QUANTITY,
  EQUIPMENT_HAS_NO_CLIENT,
  EQUIPMENT_HAS_NO_CUSTOMER_LOCATION,
  EQUIPMENT_HAS_NO_HOME_LOCATION,
  EQUIPMENT_HAS_NO_TYPE,
  EQUIPMENT_NOT_AVAILABLE,
  EQUIPMENT_NOT_WITH_CUSTOMER,
  RENTAL_CANNOT_CANCEL,
  RENTAL_CANNOT_DELIVER,
  RENTAL_CANNOT_RETURN,
} from "dora-shared";
import { t } from "./i18n";
import type { ServerRejectionParams } from "../ducks/helpers";

// Turns a refusal the server named with a code (`400 { message, code, params }`
// — see dora-shared's api-errors) into a sentence in the user's own language.
// The server's own `message` is English no matter what language the app runs
// in, so it is never what the user reads: the code picks the sentence and
// `params` fill in what it talks about (an equipment number, a status, a
// rental number, a count).
//
// Returns null for a code this build has no translation for — the caller then
// shows its own generic message rather than a blank or a raw error code.

// i18next types a key as the union of every key in the bundled resources, and
// a code only exists as a string at runtime. One cast here buys back the plain
// (key, values) signature; that every code really has a key, in every locale,
// is checked by the test beside this file.
type TranslateByKey = (
  key: string,
  values: ServerRejectionParams & { defaultValue: string }
) => string;
const translate = t as unknown as TranslateByKey;

// A `status` param is an enum name (WITH_CUSTOMER, COMPLETED). Which enum it
// is depends on the refusal, so the refusals about a rental's own status are
// listed here; every other status is an equipment status. Both lists are
// already translated for the rest of the UI, so a refusal reads the way the
// status column does.
const rentalStatusCodes: string[] = [
  RENTAL_CANNOT_DELIVER,
  RENTAL_CANNOT_RETURN,
  RENTAL_CANNOT_CANCEL,
];

// Refusals whose sentence names a unit. The server sends the unit's number, or
// an empty string for stock that has no number (bulk) — it never sends a
// database id, so there is nothing raw to leak into the sentence. "Equipment
// C-104" vs. "The equipment" is a difference of language, so it is decided
// here rather than server-side.
const equipmentCodes: string[] = [
  EQUIPMENT_NOT_AVAILABLE,
  EQUIPMENT_INSUFFICIENT_QUANTITY,
  EQUIPMENT_NOT_WITH_CUSTOMER,
  EQUIPMENT_HAS_NO_TYPE,
  EQUIPMENT_HAS_NO_CLIENT,
  EQUIPMENT_HAS_NO_CUSTOMER_LOCATION,
  EQUIPMENT_HAS_NO_HOME_LOCATION,
];

const statusText = (code: string, status: string) =>
  rentalStatusCodes.includes(code)
    ? translate(`rentals:statuses.${status}`, { defaultValue: status })
    : translate(`equipment:statuses.${status}`, { defaultValue: status });

const equipmentText = (number: string) =>
  number
    ? translate("serverErrors:equipmentNamed", {
        number,
        defaultValue: number,
      })
    : translate("serverErrors:equipment", { defaultValue: "" });

// The values the sentence interpolates, with the ones the server sends as an
// enum name or a field name turned into words first.
const interpolationValues = (
  code: string,
  params: ServerRejectionParams
): ServerRejectionParams => {
  const values: ServerRejectionParams = { ...params };
  const { status, field, equipment } = params;
  if (typeof status === "string") {
    values.status = statusText(code, status);
  }
  if (typeof field === "string") {
    values.field = translate(`serverErrors:fields.${field}`, {
      defaultValue: field,
    });
  }
  if (equipmentCodes.includes(code)) {
    values.equipment = equipmentText(
      typeof equipment === "string" ? equipment : ""
    );
  }
  return values;
};

export const serverErrorText = (
  code: string,
  params: ServerRejectionParams = {}
): string | null => {
  const text = translate(`serverErrors:${code}`, {
    ...interpolationValues(code, params),
    // A code with no translation must be distinguishable from a real one, and
    // must never surface as the key itself.
    defaultValue: "",
  });
  return text === "" ? null : text;
};
